import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apolloClient } from '@client/api/graphql/client';
import toast from 'react-hot-toast';
import {
  DeletePostMutation,
  DeletePostMutationVariables,
  DeletePostDocument,
  Post,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';

export const useDeleteCommunityPost = (communityId: number) => {
  const queryClient = useQueryClient();

  const deleteById = (postId: string) =>
    queryClient.setQueryData(
      communityQueryKeys.posts(+communityId),
      (previousData: InfiniteData<Post[]> | undefined) => {
        if (previousData?.pages && Array.isArray(previousData?.pages)) {
          return {
            ...previousData,
            pages: previousData.pages.map((page) => {
              return page.filter((post) => post.id !== postId);
            }),
          };
        }
        return previousData;
      },
    );

  return useMutation<unknown, unknown, string, VoidFunction>(
    (postId) => {
      return apolloClient.mutate<
        DeletePostMutation,
        DeletePostMutationVariables
      >({
        mutation: DeletePostDocument,
        variables: {
          communityId: +communityId,
          postId,
        },
      });
    },
    {
      onMutate: async (postId) => {
        await queryClient.cancelQueries({
          queryKey: communityQueryKeys.posts(+communityId),
        });
        const previousPosts = queryClient.getQueryData(
          communityQueryKeys.posts(+communityId),
        );
        deleteById(postId);
        return function rollback() {
          queryClient.setQueryData(
            communityQueryKeys.posts(+communityId),
            () => previousPosts,
          );
        };
      },
      onSuccess: (data, postId, rollback) => {
        rollback?.();
        deleteById(postId);
        queryClient.invalidateQueries(['communityPosts', communityId]);
        toast.success('Your post was successfully deleted!');
      },
      onError: (_error, _variables, rollback) => {
        rollback?.();
        toast.error('Something went wrong');
      },
    },
  );
};
